import {
  computeHouseAppExplorerUrl,
  Icon,
  IconDownArrowInputField,
  Image,
} from '@wome/ui'
import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import Select, {
  components,
  ControlProps,
  DropdownIndicatorProps,
  OptionProps,
  SingleValueProps,
} from 'react-select'
import HomeFilledIcon from 'ui/src/components/atoms/icons/icon/icons/HomeFilledIcon'

import s from './styles.module.scss'

import {
  HouseOwnerHouseFieldFragment,
  HousePriceTypeEnum,
  OwnerSpaceModeEnum,
  useCreateTokenOwnerAccessMutation,
} from '@/lib/graphql'

import MinHouse from '@/components/common/min-house'

import { useAuth } from '@/providers/auth/useAuth'
import { useHouse } from '@/providers/useHouse'

import HeaderMenu from './header-menu/header-menu'

const Header = ({
  isSpecialConditionsPage,
  isBookingPage,
  isAuthenticateTokenExpired = false,
}: {
  isSpecialConditionsPage?: boolean
  isBookingPage?: boolean
  isAuthenticateTokenExpired?: boolean
}) => {
  const router = useRouter()
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)
  const { house, setHouse } = useHouse()
  const { user } = useAuth()
  const { mutateAsync: generateOwnerAccessToken } =
    useCreateTokenOwnerAccessMutation()

  const ownerSpaceMode = house?.ownerSpaceMode

  const navigateWithOwnerAccess = async (url: string) => {
    generateOwnerAccessToken(
      {},
      {
        onSuccess: (data) => {
          const urlObj = new URL(url)
          urlObj.searchParams.append('ownerToken', data.createTokenOwnerAccess)
          window.open(urlObj.toString(), '_blank')
        },
      },
    )
  }

  const Option: any = ({
    data,
    ...props
  }: OptionProps<HouseOwnerHouseFieldFragment>) => {
    return (
      <components.Option {...{ ...props, data }} className={s.option}>
        {/* TODO - 3912 - Add badge with new bookings length */}
        <MinHouse house={data} style={{ cursor: 'pointer' }} />
      </components.Option>
    )
  }

  const Control = ({
    children,
    ...props
  }: ControlProps<HouseOwnerHouseFieldFragment>) => (
    <components.Control {...props}>{children}</components.Control>
  )

  const SingleValue = ({
    ...props
  }: SingleValueProps<HouseOwnerHouseFieldFragment>) => (
    <components.SingleValue {...props}>
      {house && <MinHouse house={house} style={{ cursor: 'pointer' }} />}
    </components.SingleValue>
  )

  const DropdownIndicator: any = ({ ...props }: DropdownIndicatorProps) => {
    return (
      <components.DropdownIndicator {...props}>
        <IconDownArrowInputField isActive={false} style={{ padding: 0 }} />
      </components.DropdownIndicator>
    )
  }

  return (
    <header className={s.header}>
      <div className={s.logo} onClick={() => router.push('/')}>
        <Image src='/images/logo-naboo.svg' alt='Naboo' type='contain' />
      </div>
      <div className={s.nav}>
        {user &&
          house &&
          ownerSpaceMode !== OwnerSpaceModeEnum.PREVIEW &&
          !isAuthenticateTokenExpired && (
            <>
              <Link
                href='/bookings'
                className={clsx(s.bookings, {
                  [s.bold]: router.pathname === '/bookings',
                })}
              >
                Réservations
                {/* TODO - WB-3912 - add this div when we have correct number of new clientRequest */}
                {/* <div className={s.badge}>{pendingHouseBookings.length}</div> */}
              </Link>
              {house.priceType === HousePriceTypeEnum.GLOBAL &&
                ownerSpaceMode === OwnerSpaceModeEnum.FULL && (
                  <Link
                    href='/calendar'
                    className={clsx(s.users, {
                      [s.bold]: router.pathname === '/calendar',
                    })}
                  >
                    Calendrier et prix
                  </Link>
                )}
              {ownerSpaceMode === OwnerSpaceModeEnum.FULL && (
                <>
                  <Link
                    href='/prices'
                    className={clsx(s.navTab, {
                      [s.bold]: router.pathname === '/prices',
                    })}
                  >
                    Grille tarifaire
                  </Link>

                  <Link
                    href={`/special-conditions?houseId=${house?._id}`}
                    className={clsx(s.navTab, {
                      [s.bold]: router.pathname === '/special-conditions',
                    })}
                  >
                    Mes conditions
                  </Link>
                </>
              )}
              <a
                onClick={async () =>
                  await navigateWithOwnerAccess(
                    computeHouseAppExplorerUrl({
                      house: { _id: house._id, pathname: house.pathname },
                      isSeminar: false,
                      domain: undefined,
                    }),
                  )
                }
                className={s.navTab}
                target='_blank'
                rel='noreferrer'
              >
                Mon annonce
              </a>
              <a
                onClick={async () =>
                  await navigateWithOwnerAccess(
                    `${process.env.NEXT_PUBLIC_APP_URL}/collection`,
                  )
                }
                className={s.navTab}
                target='_blank'
                rel='noreferrer'
              >
                Voir la collection
              </a>
            </>
          )}

        {user && (
          <Select
            value={house}
            placeholder={
              !house ? (
                <div className={s.emptySelector}>
                  <HomeFilledIcon /> Aucun lieu associé au compte
                </div>
              ) : (
                'Sélectionnez un logement'
              )
            }
            options={user.houses || []}
            isMulti={false}
            onChange={(value) => {
              setHouse(value as HouseOwnerHouseFieldFragment)
              isBookingPage && router.push('/bookings')
              isSpecialConditionsPage &&
                router.push(`/special-conditions?houseId=${value?._id}`)
            }}
            getOptionValue={(option) => {
              // eslint-disable-next-line react/prop-types
              return option._id
            }}
            getOptionLabel={(option) => {
              // eslint-disable-next-line react/prop-types
              return option.title
            }}
            isDisabled={!house || ownerSpaceMode === OwnerSpaceModeEnum.PREVIEW}
            isSearchable={false}
            components={{
              Option,
              Control,
              SingleValue,
              ...(ownerSpaceMode !== OwnerSpaceModeEnum.PREVIEW && {
                DropdownIndicator,
              }),
            }}
            styles={{
              control: (styles) => ({
                ...styles,
                backgroundColor: 'white',
                cursor: 'pointer',
                border: 'none',
                boxShadow: 'none',
                height: '40px',
                paddingRight: '10px',
                borderRadius: '6px',
                minWidth: '300px',
              }),
              menu: (base) => ({
                ...base,
                width: '100%',
                fontSize: '13px',
              }),
              indicatorSeparator: () => ({ display: 'none' }),
              valueContainer: (base) => ({ ...base, padding: '4px' }),
              option: (base) => ({ ...base, padding: '6px 4px' }),
            }}
          />
        )}

        {user && (
          <div
            className={s.user}
            data-testid='settings-button'
            id='menuUser'
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <Icon name='settings' size={16} />
            {isMenuOpen && (
              <HeaderMenu closeMenu={() => setIsMenuOpen(false)} />
            )}
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
